import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Mail, Linkedin, MessageCircle, Calendar, ExternalLink, AlertCircle } from 'lucide-react';
import { motion } from 'framer-motion';
import { mockData } from '../data/mock';
import { Alert, AlertDescription } from './ui/alert';

const Contact = () => {
  const { contact, instructions } = mockData;
  //const [showInstructions, setShowInstructions] = useState(true);

  return (
    <section id="contact" className="py-20 md:py-32 bg-gradient-to-br from-accent/50 to-background">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <Badge className="mb-4 bg-blue-600/10 text-blue-600 border-blue-600/20">
            Get In Touch
          </Badge>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-foreground mb-4">
            Let's Build Something Great Together
          </h2>
          <p className="text-lg md:text-xl text-foreground/70 max-w-3xl mx-auto">
            Ready to transform your business? Get a free consultation to discuss your project
          </p>
        </motion.div>


        <div className="max-w-10xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Contact Form / Google Form Embed */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <Card className="h-full border-border">
              <CardHeader>
                <CardTitle className="text-2xl">Send Me a Message</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {/* Google Form Embed */}
                <div className="w-full h-[600px] overflow-hidden rounded-lg">
                  <iframe src="https://docs.google.com/forms/d/e/1FAIpQLSdHDInOGIJPGK0Hov0-6_YqfvaF3RS8vFwp6qfTmNoF4F93Aw/viewform?embedded=true" 
                  width="100%" 
                  height="100%" 
                  frameborder="0" 
                  marginheight="0" 
                  marginwidth="0">Loading…</iframe>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Contact Info & Quick Actions */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="space-y-6"
          >
            {/* Schedule a Call Card */}
            <Card className="border-border hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <div className="flex items-start space-x-4 mb-4">
                  <div className="p-3 bg-blue-600/10 rounded-lg">
                    <Calendar className="h-6 w-6 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-foreground mb-2">
                      Schedule a Free Consultation
                    </h3>
                    <p className="text-foreground/70 mb-4">
                      Book a 30-minute call to discuss your project and get expert advice
                    </p>
                    <Button 
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                      onClick={() => {
                        window.open(contact.calendlyUrl, '_blank');
                      }}
                    >
                      Book a Call
                      <ExternalLink className="ml-2 h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Contact Methods */}
            <Card className="border-border">
              <CardContent className="p-6">
                <h3 className="text-xl font-bold text-foreground mb-6">Other Ways to Connect</h3>
                <div className="space-y-4">
                  <a
                    href={`mailto:${contact.email}`}
                    className="flex items-center space-x-3 p-4 rounded-lg hover:bg-accent transition-colors group"
                  >
                    <div className="p-2 bg-blue-600/10 rounded-lg group-hover:bg-blue-600 transition-colors">
                      <Mail className="h-5 w-5 text-blue-600 group-hover:text-white transition-colors" />
                    </div>
                    <div>
                      <p className="text-sm text-foreground/60">Email</p>
                      <p className="font-medium text-foreground">{contact.email}</p>
                    </div>
                  </a>

                  <a
                    href={contact.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center space-x-3 p-4 rounded-lg hover:bg-accent transition-colors group"
                  >
                    <div className="p-2 bg-blue-600/10 rounded-lg group-hover:bg-blue-600 transition-colors">
                      <Linkedin className="h-5 w-5 text-blue-600 group-hover:text-white transition-colors" />
                    </div>
                    <div>
                      <p className="text-sm text-foreground/60">LinkedIn</p>
                      <p className="font-medium text-foreground">Connect on LinkedIn</p>
                    </div>
                  </a>

                  <a
                    href={contact.whatsapp}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center space-x-3 p-4 rounded-lg hover:bg-accent transition-colors group"
                  >
                    <div className="p-2 bg-blue-600/10 rounded-lg group-hover:bg-blue-600 transition-colors">
                      <MessageCircle className="h-5 w-5 text-blue-600 group-hover:text-white transition-colors" />
                    </div>
                    <div>
                      <p className="text-sm text-foreground/60">WhatsApp</p>
                      <p className="font-medium text-foreground">Chat with me</p>
                    </div>
                  </a>
                </div>
              </CardContent>
            </Card>

            {/* Response Time Card */}
            <Card className="border-border bg-blue-600/5">
              <CardContent className="p-6">
                <div className="flex items-start space-x-3">
                  <div className="p-2 bg-blue-600/10 rounded-lg mt-1">
                    <AlertCircle className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-foreground mb-1">Quick Response Guarantee</h4>
                    <p className="text-sm text-foreground/70">
                      I typically respond within 24 hours on weekdays. For urgent inquiries, please mention "URGENT" in your subject line.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Contact;